/**
 * Location-scoped inventory tab.
 *
 * Renders the stock-on-hand table for a single location with quick actions
 * (Use / Receive / Adjust) so the user can manage that location's inventory
 * without leaving the location detail page. Links out to the full Inventory
 * module for catalog management, alerts, and notification settings.
 */

import {
	type InventoryStockRow,
	type InventoryTransactionType,
	listInventoryStock,
	recordInventoryTransaction,
} from "@/services/inventory-service";
import {
	ArrowRightOutlined,
	MinusOutlined,
	PlusOutlined,
	ReloadOutlined,
	SettingOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Form,
	Input,
	InputNumber,
	Modal,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface InventoryTabProps {
	locationId: number;
}

interface TxModalState {
	row: InventoryStockRow;
	type: InventoryTransactionType;
}

const TX_LABEL: Record<InventoryTransactionType, string> = {
	RECEIVE: "Receive",
	USE: "Use",
	ADJUST: "Adjust",
	TRANSFER_IN: "Transfer In",
	TRANSFER_OUT: "Transfer Out",
};

const InventoryTab = ({ locationId }: InventoryTabProps) => {
	const navigate = useNavigate();
	const [stock, setStock] = useState<InventoryStockRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [lowStockOnly, setLowStockOnly] = useState(false);
	const [txModal, setTxModal] = useState<TxModalState | null>(null);

	const fetchStock = useCallback(async () => {
		if (!locationId) return;
		setLoading(true);
		try {
			const result = await listInventoryStock({
				locationId,
				lowStockOnly,
				search,
				limit: 200,
			});
			setStock(result.stock ?? []);
		} catch (e: any) {
			console.error("Failed to load stock for location", e);
			message.error(e?.message ?? "Failed to load inventory");
		} finally {
			setLoading(false);
		}
	}, [locationId, lowStockOnly, search]);

	useEffect(() => {
		fetchStock();
	}, [fetchStock]);

	const lowStockCount = useMemo(
		() =>
			stock.filter((r) => {
				const t = r.reorderThreshold;
				return t != null && r.quantityOnHand <= t;
			}).length,
		[stock],
	);

	const totalUnits = useMemo(
		() => stock.reduce((sum, r) => sum + (r.quantityOnHand ?? 0), 0),
		[stock],
	);

	const columns: ColumnsType<InventoryStockRow> = [
		{
			title: "Item",
			dataIndex: "itemName",
			key: "itemName",
			render: (_v, row) => (
				<Space direction="vertical" size={0}>
					<Button
						type="link"
						style={{ padding: 0, height: "auto", textAlign: "left" }}
						onClick={() => navigate(`/inventory/items/${row.inventoryItemId}`)}
					>
						{row.itemName}
					</Button>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{row.itemSku ? `SKU: ${row.itemSku}` : "—"}
						{row.itemCategory ? ` · ${row.itemCategory}` : ""}
					</Typography.Text>
				</Space>
			),
		},
		{
			title: "On hand",
			dataIndex: "quantityOnHand",
			key: "quantityOnHand",
			width: 110,
			align: "right",
			sorter: (a, b) => a.quantityOnHand - b.quantityOnHand,
			render: (_v, row) => {
				const t = row.reorderThreshold;
				const low = t != null && row.quantityOnHand <= t;
				return (
					<Space>
						{low && (
							<Tooltip title={`At or below threshold (${t})`}>
								<WarningOutlined style={{ color: "#fa8c16" }} />
							</Tooltip>
						)}
						<strong style={{ color: low ? "#cf1322" : undefined }}>
							{row.quantityOnHand}
						</strong>
						<Typography.Text type="secondary">{row.itemUnit}</Typography.Text>
					</Space>
				);
			},
		},
		{
			title: "Threshold",
			dataIndex: "reorderThreshold",
			key: "reorderThreshold",
			width: 100,
			align: "right",
			render: (v) => (v != null ? v : <Typography.Text type="secondary">—</Typography.Text>),
		},
		{
			title: "Status",
			key: "status",
			width: 110,
			render: (_v, row) => {
				const t = row.reorderThreshold;
				if (t == null) return <Tag>No threshold</Tag>;
				if (row.quantityOnHand === 0) return <Tag color="red">Out of stock</Tag>;
				if (row.quantityOnHand <= t) return <Tag color="orange">Low</Tag>;
				return <Tag color="green">Healthy</Tag>;
			},
		},
		{
			title: "Preferred vendor",
			dataIndex: "preferredVendorName",
			key: "preferredVendorName",
			render: (v) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
		},
		{
			title: "Actions",
			key: "actions",
			width: 230,
			render: (_v, row) => (
				<Space size={4}>
					<Button
						size="small"
						icon={<MinusOutlined />}
						onClick={() => setTxModal({ row, type: "USE" })}
					>
						Use
					</Button>
					<Button
						size="small"
						icon={<PlusOutlined />}
						onClick={() => setTxModal({ row, type: "RECEIVE" })}
					>
						Receive
					</Button>
					<Button
						size="small"
						onClick={() => setTxModal({ row, type: "ADJUST" })}
					>
						Adjust
					</Button>
				</Space>
			),
		},
	];

	return (
		<div style={{ padding: 16 }}>
			<Card
				title={
					<Space size="large">
						<span>Inventory at this location</span>
						<Tag>{stock.length} stocked items</Tag>
						<Tag color="default">{totalUnits} units on hand</Tag>
						{lowStockCount > 0 && (
							<Tag color="orange" icon={<WarningOutlined />}>
								{lowStockCount} low-stock
							</Tag>
						)}
					</Space>
				}
				extra={
					<Space>
						<Input.Search
							placeholder="Search items..."
							allowClear
							onSearch={(v) => setSearch(v)}
							style={{ width: 220 }}
						/>
						<Tooltip title="Show only items at-or-below threshold">
							<Space>
								<Switch
									size="small"
									checked={lowStockOnly}
									onChange={setLowStockOnly}
								/>
								<Typography.Text type="secondary">Low stock only</Typography.Text>
							</Space>
						</Tooltip>
						<Button icon={<ReloadOutlined />} onClick={fetchStock} loading={loading}>
							Refresh
						</Button>
						<Button
							type="primary"
							icon={<SettingOutlined />}
							onClick={() => navigate(`/inventory?locationId=${locationId}`)}
						>
							Open Inventory
							<ArrowRightOutlined />
						</Button>
					</Space>
				}
			>
				<Table
					rowKey="id"
					size="middle"
					loading={loading}
					columns={columns}
					dataSource={stock}
					pagination={{ pageSize: 25, showSizeChanger: true }}
					locale={{
						emptyText:
							"No inventory stocked at this location yet. Use the Inventory module to add stock.",
					}}
				/>
			</Card>

			{txModal && (
				<TxQuickModal
					ctx={txModal}
					onClose={() => setTxModal(null)}
					onSubmitted={() => {
						setTxModal(null);
						fetchStock();
					}}
				/>
			)}
		</div>
	);
};

interface TxQuickModalProps {
	ctx: TxModalState;
	onClose: () => void;
	onSubmitted: () => void;
}

const TxQuickModal = ({ ctx, onClose, onSubmitted }: TxQuickModalProps) => {
	const [form] = Form.useForm();
	const [submitting, setSubmitting] = useState(false);
	const { row, type } = ctx;

	const handleOk = async () => {
		try {
			const values = await form.validateFields();
			const qty = Number(values.quantity);
			if (!Number.isFinite(qty) || qty === 0) {
				message.error("Quantity must be non-zero");
				return;
			}

			let delta: number;
			if (type === "RECEIVE") delta = Math.abs(qty);
			else if (type === "USE") delta = -Math.abs(qty);
			else delta = qty; // ADJUST keeps sign as entered

			setSubmitting(true);
			const result = await recordInventoryTransaction({
				itemId: row.inventoryItemId,
				locationId: row.locationId,
				transactionType: type,
				quantityDelta: delta,
				notes: values.notes ? String(values.notes) : undefined,
			});

			let msg = `${TX_LABEL[type]} recorded — now ${result.stock.quantityOnHand} on hand`;
			if (result.alertCreated) msg += " · low-stock alert opened";
			message.success(msg);
			onSubmitted();
		} catch (e: any) {
			if (e?.errorFields) return; // antd form validation
			console.error("Transaction failed", e);
			message.error(e?.message ?? "Transaction failed");
		} finally {
			setSubmitting(false);
		}
	};

	const explainer = (() => {
		if (type === "RECEIVE") return "Adds stock. Use this when a shipment arrives.";
		if (type === "USE")
			return "Removes stock. Use this when an item is taken from the shelf.";
		return "Manual count adjustment. Enter a signed delta (e.g. -2 to remove 2).";
	})();

	return (
		<Modal
			title={`${TX_LABEL[type]} — ${row.itemName}`}
			open
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={submitting}
			okText={`Record ${TX_LABEL[type]}`}
			destroyOnClose
		>
			<Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
				{explainer} Currently <strong>{row.quantityOnHand}</strong> {row.itemUnit} on hand.
			</Typography.Paragraph>
			<Form form={form} layout="vertical" preserve={false}>
				<Form.Item
					label={
						type === "ADJUST"
							? "Quantity (signed delta)"
							: `Quantity to ${TX_LABEL[type].toLowerCase()}`
					}
					name="quantity"
					rules={[{ required: true, message: "Quantity is required" }]}
					initialValue={type === "ADJUST" ? 0 : 1}
				>
					<InputNumber
						style={{ width: "100%" }}
						min={type === "ADJUST" ? -100000 : 1}
						step={1}
						autoFocus
					/>
				</Form.Item>
				<Form.Item label="Notes (optional)" name="notes">
					<Input.TextArea
						rows={3}
						placeholder={
							type === "USE"
								? 'e.g. "Replaced fan motor on walk-in #2"'
								: type === "RECEIVE"
									? 'e.g. "Received from Vendor X — PO #1234"'
									: 'e.g. "Manual count correction after audit"'
						}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export { InventoryTab };
